import React from 'react';
import _ from 'underscore';

import {pluralize} from 'BenchlingUtil';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FileStates from 'FileStates';
import * as NoteLinkedFileDAO from 'notebook/notes/attachments/NoteLinkedFileDAO';
import {FromServerNoteLinkedFile} from 'notebook/util/NoteUtilConstants';
import notifications, {Response} from 'notifications';
import {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  ATTACHMENT_UPLOAD_STARTED_EVENT,
  ATTACHMENT_UPLOAD_FINISHED_EVENT,
  composeAttachmentUploadEventMetadata,
} from 'performance/processNotebookEvents';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import * as ImportUtil from 'util/ImportUtil';
import {typeToName} from 'util/NameUtil';

type UploadStatusFn = (taskId: string, fileName: string, response: Response) => void;

function onUploadFailed(numFiles: number, parentTaskId: string): UploadStatusFn {
  let failed = 0;
  return function (taskId: string, fileName: string, response: Response): void {
    failed += 1;
    if (failed === numFiles) {
      TrayTaskActions.removeTrayTask(parentTaskId);
    }
    const friendlyError = notifications.getFriendlyError(response);
    const error =
      friendlyError != null
        ? friendlyError
        : `An error occurred while uploading ${fileName}. Please try again.`;
    notifications.notifyError(response, {duration: 0, id: taskId});
    TrayTaskActions.updateTrayTask(taskId, {
      progress: TrayTaskConstants.FAILURE_PROGRESS,
      subheading: 'Upload failed',
      error,
    });
  };
}

function onUploadSuccess(parentTaskId: string, noteId: string, numFiles: number): UploadStatusFn {
  let done = 0;
  return function (taskId: string, fileName: string): void {
    let fileString: string;
    done += 1;
    if (done === numFiles) {
      if (done === 1) {
        fileString = fileName;
      } else {
        fileString = `${fileName} and ${done - 1} other ${pluralize(done - 1, 'file')}`;
      }
    } else {
      fileString = `${done} out of ${numFiles} files`;
    }

    const shouldIncludeOpenButton = Boolean(FileStates.get().getFileState(noteId));
    // TODO(T4599): Open the noted linked file directly, instead of opening its containing note.
    const openFile = (): void => {
      FileBrowserActions.openFile(noteId);
    };

    logProfilerTimingEvent({...ATTACHMENT_UPLOAD_FINISHED_EVENT, id: taskId});

    TrayTaskActions.updateTrayTask(parentTaskId, {
      progress: TrayTaskConstants.SUCCESS_PROGRESS,
      // "Open" button is rendered if onHeadingClick is defined
      onHeadingClick: shouldIncludeOpenButton ? openFile : undefined,
      subheading: `Uploaded ${fileString} to ${typeToName(APIUtil.apiIdToType(noteId))}`,
      duration: 5,
    });
  };
}

// Uploads the file and returns the associated task id and deferred, through which the caller can handle the
// result.
const createTaskAndUploadFiles = function (
  noteId: string,
  files: FileList | Array<File>
): Array<{taskId: string; fileUploadDeferred: JQueryPromise<FromServerNoteLinkedFile>}> {
  let fileString: string;
  const numFiles = files.length;
  if (numFiles > 1) {
    fileString = `${numFiles} files`;
  } else if (files[0].hasOwnProperty('name')) {
    fileString = `${files[0].name}`;
  } else {
    fileString = '';
  }

  const parentTaskId = TrayTaskActions.createTrayTask({
    icon: <BenchlingIcon icon="paperclip" />,
    heading: `Uploading ${fileString}`,
    hidden: false,
  });

  const onProgress = TrayTaskActions.onMultiProgressFor(parentTaskId, numFiles);
  const onUploadSuccessFn = onUploadSuccess(parentTaskId, noteId, numFiles);
  const onUploadFailedFn = onUploadFailed(numFiles, parentTaskId);

  // eslint-disable-next-line no-restricted-syntax
  return _.map(files, (file, index) => {
    const taskId = TrayTaskActions.createTrayTask({
      icon: <BenchlingIcon icon="paperclip" />,
      heading: file.name,
      hidden: true,
    });
    logProfilerTimingEvent(
      {...ATTACHMENT_UPLOAD_STARTED_EVENT, id: taskId},
      composeAttachmentUploadEventMetadata(file)
    );
    const fileUploadDeferred = uploadForNote(
      noteId,
      file,
      taskId,
      parentTaskId,
      onProgress.bind(null, index, taskId)
    )
      .fail(onUploadFailedFn.bind(null, taskId, file.name))
      .done(onUploadSuccessFn.bind(null, taskId, file.name));

    return {taskId, fileUploadDeferred};
  });
};

const uploadForNote = function (
  noteId: string,
  file: File,
  taskId: string,
  parentTaskId: string,
  onProgress: (e: TrayTaskActions.TaskProgress) => void
): JQueryPromise<FromServerNoteLinkedFile> {
  return ImportUtil.upload(file)
    .progress(onProgress)
    .then((s3location: string) => {
      TrayTaskActions.updateTrayTask(parentTaskId, {
        onSubheadingClick() {
          FileBrowserActions.openFile(noteId);
        },
        subheading: 'Processing upload',
      });
      return NoteLinkedFileDAO.create({location: s3location});
    });
};

export default {
  createTaskAndUploadFiles,
  uploadForNote,
};
